/* eslint no-prototype-builtins: "warn" */

import config from 'config';

import {
  getMembership,
  getMembershipPeriod,
  MembershipType,
  SkipTheMonthStatus,
} from '../../../react-accounts';
import { PromoType } from '../../../react-promos';
import {
  createSelector,
  getNewDateFunction,
  parseDate,
  getSession,
  getDateNowFunction,
  toPacificDateString,
} from '../../../redux-core';
import { OrderTaxTypes } from '../constants/checkout';
import { countryList as allCountries } from '../constants/countries';
import {
  filtersToRequestParams,
  requestParamsToAggregationKey,
  requestParamsToStoreKey,
} from '../utils';
import { getProfileSizesForGrid } from '../utils/sizes';

import {
  createPaymentMethodFromId,
  createPaymentMethodFromData,
  createPaymentMethodFromOrder,
} from './paymentMethods';
import { getPromo } from './promos';
import {
  getPotentialSegmentsSelector,
  getSegmentedValue,
} from './segmentation';

export const getDefaultCountry = createSelector(
  (state) => state.domain.tld,
  (state, props = {}) => props.defaultCountry,
  (tld, defaultCountry) =>
    defaultCountry || config.get('public.domain.defaultCountries')[tld]
);

/**
 * Return the current billing address associated with the cart?.
 */
export const getCartBillingAddress = createSelector(
  (state) => state.checkout?.billingAddress,
  getDefaultCountry,
  (address, defaultCountry) =>
    address && address.countryCode
      ? address
      : { countryCode: defaultCountry, ...address }
);

export const getCartPaymentMethod = createSelector(
  (state) => state.account?.payment.paymentMethods,
  (state) =>
    state.checkout?.creditCardId ||
    state.checkout?.paymentServiceProviderId ||
    null,
  (state) => state.checkout?.paymentMethod || null,
  (state) => state.storeGroup.paymentOptions.paymentProvider,
  createPaymentMethodFromId
);

export const isEmp = createSelector(getMembership, (membership) => {
  return (
    membership.membershipTypeId === MembershipType.TOKEN ||
    membership.membershipTypeId === MembershipType.TOKEN_ANNIVERSARY
  );
});

export const isEmpVip = createSelector(
  isEmp,
  getMembership,
  (isEmp, membership) => {
    return isEmp && membership.isVip && !membership.isDowngraded;
  }
);

/**
 * Returns a *function* that will return the membership product info from
 * `membershipTypes` for the given type, but with the `productId` field
 * resolved to the numeric ID for the appropriate domain. A `type` field will
 * also be added for easy checking of which type was returned (for when this
 * object inevitably gets passed around, or looked up indirectly).
 */
export const getMembershipProductInfoByType = createSelector(
  (state) => state.domain.tld,
  (tld) => {
    return (type) => {
      const info = config.get('public.membershipTypes')[type];
      if (info == null) {
        throw new Error(`Unknown membership type: '${type}'`);
      }
      const productId = info.productId[tld];
      if (productId == null) {
        throw new Error(`No productId is configured for domain: '${tld}'`);
      }
      return { type, ...info, productId };
    };
  }
);

/**
 * This just exists in case this is controlled dynamically (by domain, API,
 * etc.) in the future instead of hardcoded into the config.
 */
export function getDefaultMembershipType() {
  return config.get('public.defaultMembershipType');
}

export const getOrder = createSelector(
  (state, props = {}) => {
    if (props.order) {
      return props.order;
    } else if (props.orderId) {
      return state.account?.orders.ordersByID[props.orderId];
    }
  },
  (order) => {
    if (order) {
      if (order.items) {
        order = {
          ...order,
          items: normalizeOrderLineItems(order.items),
        };
      }
      if (order.orderLines) {
        order = {
          ...order,
          orderLines: normalizeOrderLineItems(order.orderLines),
        };
      }
    }
    return order;
  }
);

export const getOrderDiscounts = createSelector(
  (state, ownProps) => ownProps?.order,
  (order) => (order && order.discounts ? order.discounts : [])
);

/**
 * Returns an object containing info about the default membership product.
 */
export const getDefaultMembershipProductInfo = createSelector(
  getMembershipProductInfoByType,
  getDefaultMembershipType,
  (getInfo, type) => getInfo(type)
);

/**
 * Returns a *function* that will determine which type a membership item is
 * given its product ID. Returns undefined if it's none of the known types.
 */
export const getMembershipProductTypeById = createSelector(
  (state) => state.domain.tld,
  (tld) => {
    return (productId) => {
      return Object.keys(config.get('public.membershipTypes')).find((type) => {
        return (
          config.get('public.membershipTypes')[type].productId[tld] ===
          productId
        );
      });
    };
  }
);

export const isBilledEmpVip = createSelector(
  isEmpVip,
  getMembershipPeriod,
  (isEmpVip, membershipPeriod) => {
    return (
      isEmpVip &&
      membershipPeriod.statusCode === SkipTheMonthStatus.EMP_BILLED &&
      membershipPeriod.networkStatus.isUpToDate
    );
  }
);

export const filterShippingOptionsByAddress = (shippingOptions, address) => {
  return shippingOptions.filter((option) => {
    if (
      option.shippingCountries &&
      option.shippingCountries.length &&
      !option.shippingCountries.includes(address.countryCode)
    ) {
      return false;
    }
    if (
      option.excludedCountries &&
      option.excludedCountries.includes(address.countryCode)
    ) {
      return false;
    }
    if (
      option.excludedStates &&
      option.excludedStates.includes(address.state)
    ) {
      return false;
    }
    return true;
  });
};

export const getLineItems = createSelector(
  (state, props = {}) =>
    props.order
      ? props.order.items || props.order.orderLines || []
      : state.checkout?.items,
  (lineItems) => lineItems
);

export const getDefaultPaymentData = createSelector(
  (state) => state.account?.payment.paymentMethods,
  (payments) => payments.find((payment) => payment.isDefault) || payments[0]
);

export const getBillingAddress = createSelector(
  getOrder,
  getCartBillingAddress,
  (order, cartBillingAddress) =>
    order ? order.billingAddress : cartBillingAddress
);

export const getPaymentMethod = createSelector(
  getOrder,
  getCartPaymentMethod,
  (state) => state.checkout?.inProgressPaymentInfo,
  (state) => state.storeGroup.paymentOptions.paymentProvider,
  (order, cartPaymentMethod, inProgressPaymentInfo, paymentProvider) => {
    if (order) {
      return createPaymentMethodFromOrder(order, paymentProvider);
    }
    return inProgressPaymentInfo || cartPaymentMethod;
  }
);

export const getCartShippingAddress = createSelector(
  (state) => state.checkout?.shippingAddress,
  getDefaultCountry,
  (address, defaultCountry) => {
    return address && address.countryCode
      ? address
      : { countryCode: defaultCountry, ...address };
  }
);

export const getStoreShippingOptions = createSelector(
  (state) => state.storeGroup.shippingOptions,
  (shippingOptions) => shippingOptions.options || []
);

function normalizeOrderLineItems(items) {
  return items.map((item) => ({
    ...item,
    lineId: item.lineId || item.orderLineId,
    lineItems: [item],
  }));
}

export const getCartShippingOptionId = createSelector(
  (state) => state.checkout,
  (cart) => cart?.shippingOptionId
);

export const getCartShippingOptions = createSelector(
  (state) => state.checkout?.shippingOptions,
  (shippingOptions) => shippingOptions
);

export const getShippingOptionId = createSelector(
  getOrder,
  getCartShippingOptionId,
  (order, id) => (order ? order.shippingOptionId : id)
);

export const getShippingOption = createSelector(
  getOrder,
  getShippingOptionId,
  getCartShippingOptions,
  getStoreShippingOptions,
  (order, id, shippingOptions, storeShippingOptions) => {
    if (!id) {
      return {};
    }

    let shippingOption;

    if (order && order.shippingAddress) {
      shippingOptions = storeShippingOptions;

      shippingOption = shippingOptions.find(
        (option) =>
          option.shippingOptionId === id &&
          !option.excludedCountries.includes(order.shippingAddress.countryCode)
      );
    } else {
      shippingOption = shippingOptions.find(
        (option) => option.shippingOptionId === id
      );
    }

    if (!shippingOption) {
      shippingOption = { shippingOptionId: id, incomplete: true };
    }

    return shippingOption;
  }
);

/**
 * Return the current shipping address associated with the cart, using
 * `getAddress` to determine the result.
 */
export const getShippingAddress = createSelector(
  getOrder,
  getCartShippingAddress,
  (order, cartShippingAddress) =>
    order ? order.shippingAddress : cartShippingAddress
);

/**
 * Return an object with `address` and `shippingMethod` properties.
 */
export const getShippingInfo = createSelector(
  getOrder,
  getShippingAddress,
  getShippingOption,
  (state) =>
    state.customerDetails.shipping_city &&
    state.customerDetails.shipping_city.value,
  (state) =>
    state.customerDetails.shipping_state &&
    state.customerDetails.shipping_state.value,
  (state) =>
    state.customerDetails.shipping_zip &&
    state.customerDetails.shipping_zip.value,
  (
    order,
    address,
    shippingMethod,
    customerDetailCity,
    customerDetailState,
    customerDetailZip
  ) => {
    if (order) {
      return {
        address,
        shippingMethod,
      };
    }
    return {
      address: {
        ...address,
        city: address.city || customerDetailCity,
        state: address.state || customerDetailState,
        zip: address.zip || customerDetailZip,
      },
      shippingMethod,
    };
  }
);

/**
 * Return an object with `address` and `paymentMethod` properties.
 */
export const getPaymentInfo = createSelector(
  getBillingAddress,
  getPaymentMethod,
  (address, paymentMethod) => ({ address, paymentMethod })
);

export const shouldShowCancellationNoticeForm = createSelector(
  getShippingAddress,
  (address) => {
    return (
      address?.countryCode === 'US' &&
      config.get('public.cancellationNoticeStates').includes(address?.state)
    );
  }
);

export const getDefaultAddress = createSelector(
  (state) => state.account?.addresses.data,
  (state, props = {}) => props.allowEmptyAddress !== false,
  getDefaultCountry,
  (addresses, allowEmptyAddress, defaultCountry) => {
    let address =
      addresses.find((address) => address.isDefault) || addresses[0];
    if (!address && !allowEmptyAddress) {
      address = { countryCode: defaultCountry };
    }
    return address;
  }
);

export const getDefaultPaymentMethod = createSelector(
  getDefaultPaymentData,
  (state) => state.storeGroup.paymentOptions.paymentProvider,
  createPaymentMethodFromData
);

export const getFilteredShippingOptions = createSelector(
  getCartShippingOptions,
  (state, props = {}) => props.address || getCartShippingAddress(state, props),
  filterShippingOptionsByAddress
);

/**
 * Get the cart line item that matches the VIP membership product.
 */
export const getMembershipCartItem = createSelector(
  getLineItems,
  getMembershipProductTypeById,
  (items, getType) => {
    return items?.find((item) => getType(item.productId) != null);
  }
);

export const isUnlockedXclusiveProductVisible = createSelector(
  isBilledEmpVip,
  isEmp,
  getMembershipPeriod,
  (isBilledEmpVip, isEmp, membershipPeriod) => {
    if (!membershipPeriod.networkStatus.isUpToDate) {
      return true;
    }
    return isBilledEmpVip || !isEmp;
  }
);

/**
 * A convenience function for returning just the product ID of the default
 * membership product. Use `getDefaultMembershipProductInfo` to get everything.
 */
export const getDefaultMembershipProductId = createSelector(
  getDefaultMembershipProductInfo,
  (info) => info.productId
);

export const hasOnlyMembershipCartItem = createSelector(
  getLineItems,
  getMembershipProductTypeById,
  (items, getType) => {
    // Find potentially multiple membership items, in case multiple types get
    // added by mistake or happenstance.
    return (
      items?.length > 0 &&
      items?.every((item) => getType(item.productId) != null)
    );
  }
);

export const hasPromoConflict = createSelector(
  (state) => state.checkout?.promoConflicts,
  (promoConflicts) => {
    return promoConflicts && !!promoConflicts.length;
  }
);

export const isVipLiveShoppingPromoAppliedSelector = createSelector(
  (state) => state.checkout?.cartLineDiscounts,
  (cartLineDiscounts) => {
    return !!getPromo(
      cartLineDiscounts,
      null,
      config.get('public.promoCodes.vipLiveShopping').promoCode
    );
  }
);

export const getGwpPromo = createSelector(
  [(state) => getPotentialSegmentsSelector(state), getOrderDiscounts],
  (segments, orderDiscounts) => {
    // messy lookup that can be improved
    if (orderDiscounts.length) {
      const gwpPromosByCode = {};
      Object.values(config.get('public.promoCodes.giftWithPurchase')).forEach(
        (promo) => {
          gwpPromosByCode[promo.promoCode] = promo;
        }
      );
      const gwpPromoCodes = Object.keys(gwpPromosByCode);

      const orderGwpPromo = orderDiscounts.find((discount) => {
        return gwpPromoCodes.includes(discount.promoCode);
      });

      if (orderGwpPromo) {
        return gwpPromosByCode[orderGwpPromo.promoCode];
      }
    }

    return getSegmentedValue({
      options: config.get('public.promoCodes.giftWithPurchase'),
      segments,
    });
  }
);

export const hasMembershipInCart = createSelector(
  getLineItems,
  getMembershipProductTypeById,
  (items, getType) => {
    return items?.some((item) => getType(item.productId) != null);
  }
);

export const getProductPromos = createSelector(
  (state, ownProps) => ownProps.product,
  (product) => {
    let { promos } = product;
    if (Array.isArray(promos)) {
      promos = promos[0];
    }
    if (!promos) {
      promos = {};
    }
    return promos;
  }
);

export function isProductSet(masterProduct = {}) {
  // product type id comes back in two different shapes
  const productTypeId =
    masterProduct.productTypeId || masterProduct.product_type_id;
  return productTypeId === config.get('public.productTypes.setId');
}

export const checkPreorderForSingleItem = ({ product, now }) => {
  if (product.master_product_id && product.date_preorder_expires) {
    return now() <= parseDate(product.date_preorder_expires);
  } else if (product.masterProductId && product.datePreorderExpires) {
    return now() <= parseDate(product.datePreorderExpires);
  }
  return false;
};

export const checkPreorderForSets = ({ product, now }) => {
  const itemsInSet = product.products_in_set || product.bundleItems;
  return itemsInSet
    ? itemsInSet.some((setItem) =>
        checkPreorderForSingleItem({ product: setItem, now })
      )
    : false;
};

/* We determine if something is Preorderable by the Pre Order expiration date,
 * not looking at inventory, since it's regular inventory being ghosted.
 */
export const isPreorderable = createSelector(
  (state, props) => props.product || props.masterProduct || {},
  getNewDateFunction,
  (product, now) => {
    if (isProductSet(product)) {
      return checkPreorderForSets({ product, now });
    }

    return checkPreorderForSingleItem({ product, now });
  }
);

/**
 * We are attempting to limit the amount of data returned from products/v2.
 * This list was composed by setting up a proxy object in reactui
 * ProductContext to asses which properties are needed for grids.
 *
 * Properties of NOTE:
 * - Lots of fields added due to tracking. See `getTrackProductDetails`.
 * - related_product_id_object_list: we only check if length > 1 so we are
 *   only requesting the first 2 if available
 *   - reactui/MoreColorsLabel
 */
const DEFAULT_PRODUCT_FIELDS = [
  'alias',
  'available_quantity_any_profile',
  'available_quantity_master',
  'available_quantity_preorder_master',
  'average_overall_review_filtered_stores',
  'average_review_all_stores',
  'average_review',
  'color',
  'component_count',
  'component_product_id_object_list',
  'date_expected',
  'date_preorder_expires',
  'default_product_category_id',
  'default_product_category_label',
  'default_unit_price',
  'featured_product_location_id_list',
  'item_number',
  'label',
  'master_product_id',
  'meta_data[0]',
  'permalink',
  'product_category_id_list',
  'product_images',
  'product_option_profile_id_object_list.[0]',
  'product_type_id',
  'promos',
  'related_product_id_object_list.[0]',
  'related_product_id_object_list.[1]',
  'retail_unit_price',
  'review_count',
  'sale_unit_price',
  'short_description',
  'tag_id_list',
  'warehouse_id_object_list',
];

export const getDefaultProductFields = () => DEFAULT_PRODUCT_FIELDS;

export const isNMP = createSelector(getMembership, (membership) => {
  return membership.membershipTypeId === MembershipType.CREDIT;
});

export function domainMatchesList(domain, includeDomains, excludeDomains = []) {
  const matchesTLD = (entry) => entry === '*' || entry === domain.tld;
  // Enabled if any entries in `includeDomains` match and no entries in
  // `excludeDomains` match (including wildcard matches for both).
  return includeDomains.some(matchesTLD) && !excludeDomains.some(matchesTLD);
}

function isPromoActive(promo, date) {
  const startDate = +parseDate(promo.startDate);
  const endDate = +parseDate(promo.endDate);
  return date >= startDate && date < endDate;
}

export const getActivePromos = createSelector(
  (state) => state.domain,
  (state) => state.time.pageRenderDate,
  (domain, pageRenderDate) => {
    pageRenderDate = +parseDate(pageRenderDate);
    return Object.keys(config.get('public.promos'))
      .map((key) => config.get('public.promos')[key])
      .filter((promo) => {
        if (
          !domainMatchesList(domain, promo.includeDomains, promo.excludeDomains)
        ) {
          return false;
        }
        return isPromoActive(promo, pageRenderDate);
      });
  }
);
export const allowExclusiveCheckout = createSelector(
  getActivePromos,
  (promos) => promos.some((promo) => promo.context.allowExclusiveCheckout)
);

export const hasExclusiveProducts = createSelector(getLineItems, (products) =>
  products?.some((product) => product.isExtraExclusive)
);

export const getNonPreorderItems = createSelector(
  getLineItems,
  getNewDateFunction,
  (cartItems, now) => {
    return cartItems?.filter((item) =>
      isProductSet(item)
        ? !checkPreorderForSets({ product: item, now })
        : !checkPreorderForSingleItem({ product: item, now })
    );
  }
);

// Returns if there's any in-stock items, excluding membership
export const hasNonPreorderItems = createSelector(
  getNonPreorderItems,
  getMembershipCartItem,
  (cartItems, membershipItem) => {
    const hasMembershipItem = !!membershipItem;
    const cartItemsCount = hasMembershipItem
      ? cartItems?.length - 1
      : cartItems?.length;
    return cartItemsCount > 0;
  }
);

export const isOutOfStock = (product, now) => {
  if (
    product.productTypeId === config.get('public.productTypes.membershipId')
  ) {
    return false;
  }
  // for sets
  if (
    (product.productTypeId === config.get('public.productTypes.setId') ||
      product.productTypeId === config.get('public.productTypes.byoSetId')) &&
    product.bundleItems.length
  ) {
    return product.bundleitems?.some((item) => {
      const quantityToCheck = checkPreorderForSingleItem({ product: item, now })
        ? item.availableQuantityPreorder
        : item.availableQuantity;
      return quantityToCheck < product.quantity;
    });
  }
  // for single product
  const quantityToCheck = checkPreorderForSingleItem({ product, now })
    ? product.availableQuantityPreorder
    : product.availableQuantity;
  return quantityToCheck < product.quantity;
};

export const cartHasOutOfStockItems = createSelector(
  getLineItems,
  getNewDateFunction,
  (products, now) => products?.some((product) => isOutOfStock(product, now))
);

export const hasPreorderItems = createSelector(
  getLineItems,
  getNewDateFunction,
  (cartItems, now) => {
    return cartItems?.some((item) =>
      isProductSet(item)
        ? checkPreorderForSets({ product: item, now })
        : checkPreorderForSingleItem({ product: item, now })
    );
  }
);

export const isExclusiveSoldOut = createSelector(getActivePromos, (promos) =>
  promos?.some((promo) => promo.context.isExclusiveSoldOut)
);

export const hasFreeTrialMembershipInCart = createSelector(
  getLineItems,
  getMembershipProductTypeById,
  (items, getType) => {
    return items?.some((item) => getType(item.productId) === 'freeTrial');
  }
);

export const isGwpApplied = createSelector(
  (state, ownProps) =>
    ownProps?.order?.orderLineDiscounts || state.checkout?.cartLineDiscounts,
  getGwpPromo,
  (orderLineDiscounts, gwpPromoSegment) => {
    return !!getPromo(orderLineDiscounts, null, gwpPromoSegment?.promoCode);
  }
);

export const isGwpAvailable = createSelector(
  (state) => state.checkout?.discounts,
  getGwpPromo,
  (discounts, gwpPromoSegment) => {
    return !!getPromo(discounts, null, gwpPromoSegment?.promoCode);
  }
);

export const getGwpProduct = createSelector(
  getLineItems,
  getGwpPromo,
  (products, gwpPromoSegment) => {
    if (!products) {
      return;
    }

    return products?.find(
      (product) =>
        product.discount &&
        product.discount.promoCode === gwpPromoSegment?.promoCode
    );
  }
);

export const getIsVipCart = createSelector(
  (state) => state.checkout?.isVipCart || false,
  (isVipCart) => isVipCart
);

export const promoPriceHiddenPromoTypes = new Set([
  PromoType.BOGO,
  PromoType.BOGO_WITH_DISCOUNT,
  PromoType.BUY_X_GET_Y_OFF,
  PromoType.BUY_X_GET_EACH_Y,
]);

export function shouldShowPromoPrice(promo) {
  const { promoTypeId, promo_type_id: promoTypeIdOld } = promo || {};
  const promoType = promoTypeId || promoTypeIdOld;

  return !promoPriceHiddenPromoTypes.has(promoType);
}

export const getShouldShowPromoPrice = createSelector(
  getProductPromos,
  (state) => state.borderfree?.isBorderfreeCustomer,
  (promos, isBorderfreeCustomer) => {
    return (
      !isBorderfreeCustomer &&
      (!!promos.display_on_pdp || !!promos.displayOnPdp) &&
      shouldShowPromoPrice(promos)
    );
  }
);

export const getPriceDetails = createSelector(
  [
    (state, { product }) => product,
    (state, { product }) => getProductPromos(state, { product: product ?? {} }),
    (state, { product }) =>
      getShouldShowPromoPrice(state, { product: product ?? {} }),
    getMembership,
  ],
  (product, promos, shouldShowPromoPrice, membership) => {
    const { isVip } = membership;

    const { default_unit_price: defaultPrice, retail_unit_price: retailPrice } =
      product ?? {};
    let displayPrice;

    if (shouldShowPromoPrice) {
      displayPrice = promos.promo_price;
    } else if (isVip) {
      displayPrice = defaultPrice;
    } else {
      displayPrice = retailPrice;
    }

    return {
      displayPrice,
      shouldShowPromoPrice,
    };
  }
);

export const getFreeShippingThreshold = createSelector(
  (state) => state?.domain?.tld === '.com',
  (state) => getMembership(state).isVip,
  hasMembershipInCart,
  (state) => state.storeGroup.shippingOptions,
  (isUS, isVip, hasMembershipInCart, shippingOptions) => {
    if (isUS) {
      if (isVip) {
        return 59.95;
      } else if (hasMembershipInCart) {
        return 30.0;
      }
    }
    return shippingOptions.shippingThreshold;
  }
);

export const getMinimumQuantity = createSelector(
  (state, ownProps) => ownProps.product,
  (product) => {
    const tagIds = product.tag_id_list || product.tagIds || [];
    const hasMinimumQuantityTag = tagIds.includes(
      config.get('public.productTags.minimum3Qty')
    );
    const minimumQuantity = hasMinimumQuantityTag ? 3 : 1;
    return { hasMinimumQuantityTag, minimumQuantity };
  }
);

export const isExclusiveProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.isExtraExclusive) {
      // Flag available in cart product data
      return true;
    } else if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.xclusive')
      );
    }
    return false;
  }
);

export function normalizeAssetLink(asset) {
  let { pageUrl: link = '' } = asset.options.customVars;

  if (link.indexOf('/') !== 0) {
    link = `/${link}`;
  }

  return link;
}

export const createGetAssetData = () => {
  return createSelector(
    (state, props) =>
      props.data ||
      (state.assets.containers[props.name] &&
        state.assets.containers[props.name].data),
    (state, props) => props.name,
    (assetData, name) => {
      if (assetData) {
        /* TEMPORARY remove duplicated asset ids */
        const assetIDs = new Set();

        // Annoyingly, the returned data does not include the actual key it
        // was fetched by, both the `label` and `key` properties can differ from
        // the key used to fetch. So add a `name` property.
        const container = { ...assetData.container, name };

        const assets = assetData.assets
          .filter((asset) => {
            if (asset.filtered) {
              return false;
            }
            const isDuplicate = assetIDs.has(asset.id);
            assetIDs.add(asset.id);
            return !isDuplicate;
          })
          .map((asset) => {
            /* Process flags */
            const { customVars } = asset.options;

            // Include flags from legacy `categoryFilters` var.
            const legacyFlags = customVars.categoryFilters
              ? customVars.categoryFilters
                  .split(',')
                  .map((item) => item.trim().toLowerCase())
                  .filter((item) => item)
              : [];

            const flags = customVars.flags
              ? customVars.flags
                  .split(',')
                  .map((item) => item.trim().toLowerCase())
                  .filter((item) => item)
              : [];

            return {
              ...asset,
              flags: new Set(legacyFlags.concat(flags)),
              // Add container info to individual assets for easier filtering.
              container,
            };
          });

        assetData = {
          ...assetData,
          container,
          assets,
        };
      }
      return assetData;
    }
  );
};

// Create an instance for memoizing this particular asset.
const getProductsLinkAssetData = createGetAssetData();

export const getProductsLinkAssets = createSelector(
  (state) => getProductsLinkAssetData(state, { name: 'products_image_banner' }),
  (state, props = {}) => props.assets,
  (assetContainer, assets) => {
    if (!assets) {
      assets = assetContainer ? assetContainer.assets : [];
    }

    return assets.map((asset) => {
      const {
        label,
        categoryFilters: categoryFiltersString = '',
        fpl,
        dmgCode,
        persona,
      } = asset.options.customVars;

      const link = normalizeAssetLink(asset);
      const parsedSet = new Set(
        categoryFiltersString
          .split(',')
          .map((item) => item.trim().toLowerCase())
          .filter((item) => item)
      );

      let vipStatus;

      if (parsedSet.has('vip')) {
        vipStatus = true;
        parsedSet.delete('vip');
      }
      if (parsedSet.has('nonvip')) {
        vipStatus = false;
        parsedSet.delete('nonvip');
      }

      const hideFromBorderfree = parsedSet.has('noborderfree');
      if (hideFromBorderfree) {
        parsedSet.delete('noborderfree');
      }

      const isFeaturedCategory = parsedSet.has('featuredcategory');
      if (isFeaturedCategory) {
        parsedSet.delete('featuredcategory');
      }

      const isPromo = parsedSet.has('promo');
      if (isPromo) {
        parsedSet.delete('promo');
      }

      const isNew = parsedSet.has('newlink');
      if (isNew) {
        parsedSet.delete('newlink');
      }

      const isSpecialOffer = parsedSet.has('specialoffer');
      if (isSpecialOffer) {
        parsedSet.delete('specialOffer');
      }

      let categoryFilters = ['bras', 'undies', 'lingerie'];
      if (parsedSet.has('none')) {
        categoryFilters = [];
      } else if (parsedSet.size) {
        categoryFilters = Array.from(parsedSet);
      }

      const dmgCodes = new Set(
        (dmgCode || '')
          .toString()
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item)
      );

      const personas = new Set(
        (persona || '')
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item)
      );

      return {
        label: label || undefined, // Prefer undefined over empty string labels
        link,
        categoryFilters,
        dmgCodes,
        personas,
        fpl,
        isFeaturedCategory,
        vipStatus,
        hideFromBorderfree,
        isNew,
        isPromo,
        isSpecialOffer,
        isTemplate: false,
        asset,
      };
    });
  }
);

export const getProductsLinkAssetsByUrl = createSelector(
  getProductsLinkAssets,
  (links) => {
    return links.reduce((linksByUrl, link) => {
      if (link.link) {
        linksByUrl[link.link] = link;
      }
      return linksByUrl;
    }, {});
  }
);

export const getClearanceFplId = createSelector(
  // Defined later in this file - for now, wrap in function instead of moving
  // for easier diff/review purposes...
  (state, props) => getProductsLinkAssetsByUrl(state, props),
  (state) => getMembership(state).isVip,
  (productsLinkAssetsByUrl, isVip) => {
    const saleAsset =
      productsLinkAssetsByUrl[isVip ? '/sale' : '/sale-secondary'];
    if (saleAsset && saleAsset.fpl) {
      return saleAsset.fpl;
    }
  }
);

export const isClearanceItem = createSelector(
  getClearanceFplId,
  (state, props) => props.product,
  (state) => state.domain.tld,
  (clearanceFplId, product = {}, tld) => {
    const fplIds =
      product.featured_product_location_id_list ||
      product.featuredProductLocationIdList
        ? product.featured_product_location_id_list ||
          product.featuredProductLocationIdList
        : product.fplIds;
    if (fplIds) {
      // List available in product API data
      return fplIds.includes(clearanceFplId);
    }
    return false;
  }
);

export const isMembershipItem = createSelector(
  getMembershipCartItem,
  (state, props) => props.product,
  (membershipItem, product) => membershipItem?.productId === product?.productId
);

/* Warehouse
 * warehouse ids map to a specific region. regions determine low stock thresholds.
 */
export const getRegion = createSelector(
  (state) => state.borderfree?.isBorderfreeCustomer,
  (state) => state.domain.tld,
  (state) => state.intl.geoCountry,
  (isBorderfreeCustomer, tld, geoCountry) => {
    if (isBorderfreeCustomer) {
      return 'BF';
    } else if (tld === '.com') {
      return geoCountry === 'CA' ? 'CA' : 'US';
    } else if (tld === '.co.uk') {
      return 'UK';
    }
    return 'EU';
  }
);

export const getDomainCurrency = createSelector(
  (state) => state.domain.tld,
  (tld) => config.get('public.intl.currencies')[tld]
);

export const getPreorderShipDate = createSelector(
  (state, props) => props.product,
  getNewDateFunction,
  (product, now) => {
    // for sets, preorder expire date will be calculated on back end
    // and it will return the latest date among all items in the set
    const preorderExpires =
      product?.date_preorder_expires || product?.datePreorderExpires;
    return preorderExpires && now() <= parseDate(preorderExpires)
      ? preorderExpires
      : null;
  }
);

export const getPromocode = createSelector(
  (state) => state.checkout.discounts,
  (discounts) =>
    discounts && discounts[0] ? discounts[0].promoCode : undefined
);

export const hasCustomizableItems = createSelector(
  getLineItems,
  (cartItems) => {
    return cartItems.some((item) =>
      item && item.tagIds
        ? item.tagIds.includes(config.get('public.productTags.customizable'))
        : false
    );
  }
);

export const getExcludedProductTypesForAfterpay = createSelector(
  hasPreorderItems,
  hasCustomizableItems,
  (hasPreorder, hasCustomizable) => {
    if (hasPreorder) {
      return 'pre-order items';
    }
    if (hasCustomizable) {
      return 'customizable items';
    }
    return null;
  }
);

export const getActiveGiftCertCredits = createSelector(
  (state) => state?.checkout?.credits ?? [],
  (credits) =>
    credits.reduce((giftCardCreditList, credit) => {
      if (credit.creditType === 'GC') {
        giftCardCreditList.push(credit);
      }
      return giftCardCreditList;
    }, [])
);

export function getDiscountsByPromo(discounts) {
  // Find the same promo to get the total discount amount applied per promo
  // Find the same promo to get the total discount amount applied per promo
  const discountMap = discounts.reduce((result, discount) => {
    // create a copy of the discount so that we're not directly manipulating any of it
    const orderDiscount = { ...discount };
    const sameDiscount = result.get(orderDiscount.promoId);
    if (sameDiscount) {
      sameDiscount.amount += orderDiscount.amount;
    } else {
      result.set(orderDiscount.promoId, orderDiscount);
    }
    return result;
  }, new Map());

  // Map.values does not work in IE11, polyfill does not work for some reason.
  const values = [];
  discountMap.forEach((value) => values.push(value));
  return values;
}

export const getDiscounts = createSelector((state, ownProps) => {
  const activeDiscounts = state.checkout.discounts.filter(
    (discount) => discount.amount > 0
  );
  const orderDiscounts =
    ownProps.order && ownProps.order.orderLineDiscounts
      ? ownProps.order.orderLineDiscounts
      : [];

  return activeDiscounts.length ? activeDiscounts : orderDiscounts;
}, getDiscountsByPromo);

// taxType of 'delivery' will only be available for US with state of Colorado
export const getOrderRetailDeliveryFee = createSelector(
  (state) => state.checkout,
  (_, ownProps) => ownProps?.order,
  (checkout, orderFromProps) => {
    if (orderFromProps) {
      return orderFromProps.deliveryTax || null;
    }

    return (
      checkout?.cartTax?.find((tax) => tax.taxType === OrderTaxTypes.DELIVERY)
        ?.taxableAmount || null
    );
  }
);

export const getStoreCredits = createSelector(
  getOrder,
  (state) => state.checkout,
  (order, cart) => {
    if (!order) {
      order = cart;
    }
    const storeCreditTotal = order.storeCreditTotal;
    const membershipStoreCredit = order.memberCreditAmount;
    const useMembershipStoreCredit = membershipStoreCredit > 0;
    const storeCreditsOnly = order.storeCreditAmount;
    const useStoreCredit = storeCreditsOnly > 0;

    return {
      useStoreCredit,
      useMembershipStoreCredit,
      storeCreditTotal,
      membershipStoreCredit,
      storeCreditsOnly,
    };
  }
);

// Whole number based on how many credits were used
export const getCreditsApplied = createSelector(
  (state) => state.checkout.memberCreditAmount,
  (state) => state.membership.price,
  (memberCreditAmount, membershipPrice) =>
    Math.round(memberCreditAmount / membershipPrice)
);

export const getStorePaymentTypes = createSelector(
  (state) => state.storeGroup.paymentOptions.paymentTypes,
  (state) => state.domain.tld,
  (state) => getMembership(state).isVip,
  (state) => state.borderfree?.isBorderfreeCustomer,
  (state) => getMembership(state).networkStatus.isUpToDate,
  (_, { isAfterPayEnabled }) => isAfterPayEnabled,
  (_, { isCashAppEnabled }) => isCashAppEnabled,
  (_, { isKlarnaEnabled }) => isKlarnaEnabled,
  (
    paymentTypes,
    domain,
    isVip,
    isBorderfreeCustomer,
    isMembershipInitialized,
    isAfterPayEnabled,
    isCashAppEnabled,
    isKlarnaEnabled
  ) => {
    let filteredPaymentTypes = paymentTypes.map(
      // Rename `cc` type.
      (paymentType) => (paymentType === 'cc' ? 'creditcard' : paymentType)
    );
    filteredPaymentTypes = filteredPaymentTypes.filter(
      (value) => value !== 'afterpay'
    );
    const shouldDisplayAfterpay =
      isMembershipInitialized && isAfterPayEnabled && !isBorderfreeCustomer;

    const shouldDisplayKlarna = isMembershipInitialized && isKlarnaEnabled;

    const shouldDisplayCashApp = isCashAppEnabled && !isBorderfreeCustomer;

    if (!config.get('public.paypal.activated')[domain]) {
      filteredPaymentTypes = filteredPaymentTypes.filter(
        (value) => value !== 'paypal'
      );
    }
    if (shouldDisplayAfterpay) {
      filteredPaymentTypes.push('afterpay');
    }
    if (!shouldDisplayKlarna) {
      filteredPaymentTypes = filteredPaymentTypes.filter(
        (value) => value !== 'klarna'
      );
    }
    if (!shouldDisplayCashApp) {
      filteredPaymentTypes = filteredPaymentTypes.filter(
        (value) => value !== 'cashapppay'
      );
    }
    return filteredPaymentTypes || [];
  }
);

export const getStorePaymentSettings = createSelector(
  (state) => state.storeGroup.paymentOptions.paymentSettings,
  (paymentSettings) => {
    if (!paymentSettings) {
      return {};
    }
    // Rename `cc` field.
    const { cc, ...rest } = paymentSettings;
    return { creditcard: cc, ...rest };
  }
);

/**
 * Returns a *function* that will return membership product info for a
 * membership item given its product ID.
 */
export const getMembershipProductInfoById = createSelector(
  getMembershipProductTypeById,
  getMembershipProductInfoByType,
  (getType, getInfo) => {
    return (productId) => {
      const type = getType(productId);
      if (type) {
        return getInfo(type);
      }
    };
  }
);

export const getShippingCountries = createSelector(
  (state) => state.storeGroup.shippingOptions,
  (shippingOptions) => shippingOptions.shippingCountries || []
);
export const getCountryList = createSelector(
  (state, props) => props.addressType,
  (state, props) => props.countryList,
  getShippingCountries,
  (addressType, countryList, shippingCountries) => {
    if (countryList) {
      return countryList;
    }
    if (addressType === 'shipping') {
      const shippingCountriesSet = new Set(shippingCountries);
      // We could just return `shippingCountries`, but this will filter out
      // anything bogus and preserve the ordering we want.
      return allCountries.filter((country) =>
        shippingCountriesSet.has(country)
      );
    }
    return allCountries;
  }
);

export const getUserProfileProductFilters = createSelector(
  (state, props) => props.filters,
  (state, props) => props.applyUserSizes,
  (state) => state.customer,
  (filters, applyUserSizes, profile) => {
    const {
      defaultTagId,
      optionSignatures = [],
      braSizes = [],
      braletteSizes = [],
      lingerieSizes = [],
      undieSizes = [],
    } = filters;
    // Only add user sizes if `applyUserSizes` prop is true and the filters do
    // not already include size selections (including `defaultTagId`).
    if (
      applyUserSizes &&
      !optionSignatures.length &&
      !braSizes.length &&
      !braletteSizes.length &&
      !lingerieSizes.length &&
      !undieSizes.length &&
      !defaultTagId
    ) {
      const userSizes = getProfileSizesForGrid(profile, filters);

      if (Object.keys(userSizes).length) {
        if (filters.categorized) {
          return {
            ...filters,
            ...userSizes,
          };
        }
        return {
          ...filters,
          optionSignatures: Object.values(userSizes),
        };
      }
    }
    return filters;
  }
);

export const getExcludedTagIds = ({
  fpls = null,
  isVip,
  includeFplOnly = Boolean(fpls),
  includeCollectibles = false,
  isUnlockedXclusiveProductVisible = false,
}) => {
  const fplOnlyTagId = config.get('public.productTags.fplOnly');
  const collectibleTag = config.get('public.productTags.collectible');
  const leadOnlyTagId = config.get('public.productTags.leadOnly');
  const vipOnlyTagId = config.get('public.productTags.vipOnly');
  const unlockedXclusiveTag = config.get('public.productTags.unlockedXclusive');
  const excludeTagIds = [];
  if (!includeFplOnly) {
    excludeTagIds.push(fplOnlyTagId);
  }

  if (!includeCollectibles) {
    excludeTagIds.push(collectibleTag);
  }

  if (isVip) {
    excludeTagIds.push(leadOnlyTagId);
  } else {
    excludeTagIds.push(vipOnlyTagId);
  }

  if (!isUnlockedXclusiveProductVisible) {
    excludeTagIds.push(unlockedXclusiveTag);
  }

  return excludeTagIds;
};

export const getStateDependentProductFilters = createSelector(
  getUserProfileProductFilters,
  getClearanceFplId,
  (state) => state.domain.tld,
  (state) => state.borderfree?.isBorderfreeCustomer,
  (state) => getMembership(state).isLead,
  (state) => getMembership(state).isVip,
  (state, props) => !!props.isFavoritesContext,
  isUnlockedXclusiveProductVisible,
  (
    filters,
    clearanceFplId,
    tld,
    isBorderfreeCustomer,
    isLead,
    isVip,
    isFavoritesContext,
    isUnlockedXclusiveProductVisible
  ) => {
    // set excludeTagIds to be empty array for favorites page
    // since all the products the user favorited should be seen in favorites page
    // for example vips can see lead only products, and they can favorite them
    const excludeTagIds = isFavoritesContext
      ? []
      : getExcludedTagIds({
          fpls: filters.fpls,
          isVip,
          includeFplOnly: filters.includeFplOnly, // override the default includeFplOnly if we pass one
          includeXclusive: filters.includeXclusive,
          isUnlockedXclusiveProductVisible,
        });

    const hideProduct = config.get('public.featuredProductLists.hideProduct');
    const featuredProductLists = config.get('public.featuredProductLists');
    const excludeFpls = [hideProduct[tld]];
    if (filters.excludeFpls) {
      filters.excludeFpls.forEach((fpl) => {
        const idsByDomain = featuredProductLists[fpl];
        if (idsByDomain) {
          // If found, the fpl was a named alias for which we'll resolve the ID
          // via the config based on the current TLD.
          if (idsByDomain[tld]) {
            excludeFpls.push(idsByDomain[tld]);
          }
        } else {
          // Otherwise, assume the fpl was already an ID.
          excludeFpls.push(fpl);
        }
      });
    }

    if (
      clearanceFplId &&
      filters.fpls !== clearanceFplId &&
      !isFavoritesContext
    ) {
      excludeFpls.push(clearanceFplId);
    }

    const borderfreeWarehouse = config.get('public.borderfree.warehouseId');

    return {
      ...filters,
      excludeFpls: Array.from(new Set(excludeFpls)),
      excludeTagIds: Array.from(new Set(excludeTagIds)),
      warehouseId: isBorderfreeCustomer ? borderfreeWarehouse : null,
    };
  }
);

/**
 * Returns a *function* that will return the product ID of the given membership
 * type. This is just a convenience function for retrieving only the ID, use
 * `getMembershipProductInfoByType` to get everything.
 * `getMembershipProductInfoByType` to get everything.
 */
export const getMembershipProductIdByType = createSelector(
  getMembershipProductInfoByType,
  (getInfo) => {
    return (type) => getInfo(type).productId;
  }
);

/**
 * Convenience function for compatibility with older selectors/components.
 */
export const getFreeTrialMembershipProductId = createSelector(
  getMembershipProductIdByType,
  (getId) => getId('freeTrial')
);

export const getMembershipCartItemInfo = createSelector(
  getMembershipCartItem,
  getMembershipProductInfoById,
  (membershipItem, getInfoById) => {
    return membershipItem && getInfoById(membershipItem.productId);
  }
);

export function groupLineItems(items) {
  const productTypeSetId = config.get('public.productTypes.setId');

  const productMap = items.reduce((result, item) => {
    let product;
    if (item.productTypeId === productTypeSetId) {
      product = result.get(item.groupKey);
      product = result.get(item.groupKey);
    } else {
      product = result.get(item.productId);
      product = result.get(item.productId);
    }
    if (product) {
      product.lineItems.push(item);
      product.quantity += 1;
    } else {
      // TODO: Figure out which other properties are line item specific.
      const { lineId, orderLineId, dateAdded, ...rest } = item;
      product = {
        ...rest,
        lineItems: [item],
        quantity: 1,
      };
      if (item.productTypeId === productTypeSetId) {
        result.set(item.groupKey, product);
      } else {
        result.set(item.productId, product);
      }
    }
    return result;
  }, new Map());

  // Map.values does not work in IE11, polyfill does not work for some reason.
  const values = [];
  productMap.forEach((value) => values.push(value));
  return values;
}

/**
 * Get an array of cart products with `lineItems` and `quantity` properties.
 */
export const getGroupedCartItems = createSelector(getLineItems, groupLineItems);

export const isXtraVipBoxInCart = createSelector(
  (state) => state.checkout.items,
  (items) => {
    const xtraVipBoxTag = config.get('public.productTags.xtraVipBox');
    return items.some(
      (item) =>
        item.tagIds && item.tagIds.includes(xtraVipBoxTag) && isProductSet(item)
    );
  }
);

export const hasMultipleOrders = createSelector(
  (state, props) =>
    props.order &&
    props.order.splitOrders &&
    props.order.splitOrders.length > 1,
  (multipleOrders) => {
    return multipleOrders;
  }
);

export const filterPaymentTypesByAddress = (
  paymentTypes,
  paymentSettings,
  address
) => {
  return paymentTypes.filter((paymentType) => {
    const settings = paymentSettings[paymentType];
    switch (paymentType) {
      case 'sepa':
        if (!settings) {
          return false;
        }
        return (
          settings.countries.includes(address.countryCode) &&
          !settings.excludedCountries.includes(address.countryCode)
        );
      default:
        return true;
    }
  });
};

export const getCartPaymentTypes = createSelector(
  (state, { isAfterPayEnabled, isCashAppEnabled, isKlarnaEnabled }) =>
    getStorePaymentTypes(state, {
      isAfterPayEnabled,
      isCashAppEnabled,
      isKlarnaEnabled,
    }),
  getStorePaymentSettings,
  getCartShippingAddress,
  filterPaymentTypesByAddress
);

export const hasInvalidCountry = createSelector(
  (state, props) => props.address,
  getCountryList,
  (address, countryList) => {
    if (countryList && address.countryCode) {
      return !countryList.includes(address.countryCode);
    }
    return !address.countryCode;
  }
);

export const onlyHasPreorderItems = createSelector(
  getLineItems,
  getNewDateFunction,
  (cartItems, now) => {
    return cartItems.every((item) => {
      // parseDate converts null values to 1969.
      return item.datetimePreorderExpires &&
        item.datetimePreorderExpires !== null
        ? now() <= parseDate(item.datetimePreorderExpires)
        : false;
    });
  }
);

/* for influencer we need set items to be counted as 1 each */
export const getInfluencerItemCountExcludingMembership = createSelector(
  (state) => state.checkout.items,
  getMembershipCartItem,
  (items, membershipItem) => {
    let counter = 0;
    if (!items) {
      return 0;
    }
    items.forEach((item) => {
      counter += item.bundleItems ? item.bundleItems.length : 1;
    });
    return membershipItem ? counter - 1 : counter;
  }
);

/* NavBar Shopping Bag Icon should not count the membership */
export const getItemCountExcludingMembership = createSelector(
  (state) => state.checkout.cartProviderType,
  (state) => state.checkout.itemCount,
  getMembershipCartItem,
  (cartProviderType, itemCount, membershipItem) => {
    // ECHO carts do not include membership items in itemCount, so
    // just return it right away.
    if (cartProviderType === 'ECHO') {
      return itemCount;
    }
    return membershipItem ? itemCount - 1 : itemCount;
  }
);

export const getFreeTrialDetails = createSelector(
  (state) =>
    getMembership(state).membershipTrials &&
    getMembership(state).membershipTrials.length
      ? getMembership(state).membershipTrials
      : [],
  (membershipTrials) => {
    const freeTrialStatusCodes = new Set(
      config.get('public.freeTrialStatusCodes')
    );
    const freeTrialDetails = membershipTrials.find((freeTrial) => {
      return freeTrialStatusCodes.has(freeTrial.statusCode);
    });

    return freeTrialDetails;
  }
);

export const getAccountMembershipProductType = createSelector(
  (state) => getMembership(state).isVip,
  (state) => getMembership(state).membershipTypeLabel,
  getFreeTrialDetails,
  (isVip, membershipTypeLabel, freeTrialDetails) => {
    if (isVip) {
      switch (membershipTypeLabel) {
        case 'annual':
          return freeTrialDetails ? 'freeTrial' : 'annual';
        case 'monthly':
          return 'monthly';
      }
    }
  }
);

export const getAccountMembershipProductInfo = createSelector(
  getAccountMembershipProductType,
  getMembershipProductInfoByType,
  (type, getInfo) => {
    if (type) {
      return getInfo(type);
    }
  }
);

export const getByoSaleFplIds = createSelector(
  (state) =>
    state.dynamicRoutes?.routeInfoByPath?.['/byo/sale']?.data?.productJson
      ?.backgroundFplIds,
  (byoSaleFpls) => {
    return byoSaleFpls || [];
  }
);

export const getWarehouseIds = createSelector(
  (state) => getRegion(state),
  (region) => {
    return config.get('public.quantityRules.warehouseIdsByRegion')[region];
  }
);

/**
 * @import
 * @type {import('./types').getDefaultProductFilters}
 */
export const getDefaultProductFilters = createSelector(
  getClearanceFplId,
  getByoSaleFplIds,
  (state) => getMembership(state).membershipLevelGroup,
  (state) => state.borderfree?.isBorderfreeCustomer,
  (state) => getMembership(state).isLead,
  (state) => getMembership(state).isVip,
  (state) => state.domain.tld,
  (state, baseFilters) => baseFilters,
  (state, baseFilters, options) => options,
  isUnlockedXclusiveProductVisible,
  (state) => state.intl.geoCountry,
  getWarehouseIds,
  (
    clearanceFplId,
    byoSaleFplIds,
    membershipLevelGroup,
    isBorderfreeCustomer,
    isLead,
    isVip,
    tld,
    baseFilters,
    options = {},
    isUnlockedXclusiveProductVisible,
    geoCountry,
    warehouseIds
  ) => {
    let {
      filterByMembershipLevelGroup,
      includeOutOfStock = true,
      excludeFplList = ['hideProduct'],
      includeFplOnly = false,
      autoExcludeClearanceFplIds = true,
    } = options;
    let {
      fpls,
      excludeFpls: excludeFplsFromBaseFilters = [],
      excludeTagIds: excludeTagIdsFromBaseFilters = [],
      aggregationFilter = {},
      excludePreorderProducts,
    } = baseFilters;

    const excludeFpls = Array.from(new Set([...excludeFplsFromBaseFilters]));
    let excludeTagIds = Array.from(new Set([...excludeTagIdsFromBaseFilters]));

    const hasSizeFilters = Object.entries(aggregationFilter).some(
      ([key, value]) => {
        return key.startsWith('size') && value.length;
      }
    );

    if (hasSizeFilters) {
      includeOutOfStock = false;
    }
    getExcludedTagIds({
      fpls,
      isLead,
      isVip,
      includeFplOnly, // override the default includeFplOnly if we pass one
      isUnlockedXclusiveProductVisible,
    }).forEach((tagId) => {
      if (!excludeTagIds.includes(tagId)) {
        excludeTagIds = [...excludeTagIds, tagId];
      }
    });

    if (isBorderfreeCustomer) {
      excludePreorderProducts = true;
    }

    if (excludeFplList) {
      excludeFplList.forEach((fpl) => {
        const idsByDomain = config.get('public.featuredProductLists')[fpl];
        if (idsByDomain) {
          // If found, the fpl was a named alias for which we'll resolve the ID
          // via the config based on the current TLD.
          if (idsByDomain[tld] && !excludeFpls.includes(idsByDomain[tld])) {
            excludeFpls.push(idsByDomain[tld]);
          }
        } else {
          // Otherwise, assume the fpl was already an ID.
          excludeFpls.push(fpl);
        }
      });
    }
    // Exclude clearanceFplId from grid results, unless it is a sale or BYO sale grid
    if (
      autoExcludeClearanceFplIds &&
      clearanceFplId &&
      !(
        (baseFilters.backgroundFplIds &&
          baseFilters.backgroundFplIds.includes(clearanceFplId)) ||
        (baseFilters.fpls && baseFilters.fpls.includes(clearanceFplId)) ||
        (baseFilters.backgroundFplIds &&
          baseFilters.backgroundFplIds.some((fpl) =>
            byoSaleFplIds.includes(fpl)
          )) ||
        (baseFilters.fpls &&
          baseFilters.fpls.some((fpl) => byoSaleFplIds.includes(fpl)))
      )
    ) {
      excludeFpls.push(clearanceFplId);
    }

    return {
      ...baseFilters,
      excludeFpls: Array.from(new Set(excludeFpls)),
      excludeTagIds: Array.from(new Set(excludeTagIds)),
      ...(filterByMembershipLevelGroup && { membershipLevelGroup }),
      excludePreorderProducts,
      includeOutOfStock,
      warehouseId: geoCountry === 'CA' ? warehouseIds : null,
    };
  }
);

export const isByoSaleItem = createSelector(
  getByoSaleFplIds,
  (state, props) => props.product,
  (byoSaleFplIds, product) => {
    const fplIds =
      product.featured_product_location_id_list ||
      product.featuredProductLocationIdList
        ? product.featured_product_location_id_list ||
          product.featuredProductLocationIdList
        : product.fplIds;
    if (fplIds) {
      return fplIds.some((fplId) => byoSaleFplIds.includes(fplId));
    }
  }
);

export const TRACK_BUILD_A_SET_CLICK = 'TRACK_BUILD_A_SET_CLICK';
export const TRACK_COMPLETE_SET_CLICK = 'TRACK_COMPLETE_SET_CLICK';
export const TRACK_MEMBERSHIP_SKIP_STEP = 'TRACK_MEMBERSHIP_SKIP_STEP';
export const TRACK_MEMBERSHIP_ACTIVE_SKIP_ACTIONS =
  'TRACK_MEMBERSHIP_ACTIVE_SKIP_ACTIONS';

export function trackBuildASetButtonClick(productInfo) {
  return {
    type: TRACK_BUILD_A_SET_CLICK,
    productInfo,
  };
}

export function trackCompleteByoSet(payload, byoProductId) {
  return {
    type: TRACK_COMPLETE_SET_CLICK,
    payload,
    byoProductId,
  };
}

export function trackMembershipSkipStep(stepName) {
  return {
    type: TRACK_MEMBERSHIP_SKIP_STEP,
    stepName,
  };
}

export function trackMembershipActiveSkipActions(eventLabel) {
  return {
    type: TRACK_MEMBERSHIP_ACTIVE_SKIP_ACTIONS,
    eventLabel,
  };
}

/**
 *  Get the DM Variant Codes
 *  make sure we're in the right domain
 * verify that we have what we need and the promo hasnt expired
 */
const hasDmPromoExpired = createSelector(
  (state) => getMembership(state).membershipLevelGroupId,
  (state) => getMembership(state).dateTimeAdded,
  getDateNowFunction,
  (membershipLevel, dateTimeAdded, now) => {
    // if no dateTimeAdded the user should be anonymous
    if (!dateTimeAdded) {
      return false;
    }
    const currentDate = now();
    const expiryDate =
      parseDate(dateTimeAdded).getTime() +
      config.get('public.dmGatewayExpiration');
    const hasExpired = currentDate > expiryDate;
    if (membershipLevel === 100 && !hasExpired) {
      return false;
    } else {
      return true;
    }
  }
);

export const currentDmGateway = createSelector(
  (state) => state.domain,
  (state) => getSession(state).dmGatewayCode,
  (state) => state.borderfree?.isBorderfreeCustomer,
  hasDmPromoExpired,
  (domain, dmgCode, isBorderfreeCustomer, dmPromoExpired) => {
    if (isBorderfreeCustomer || !dmgCode || dmPromoExpired) {
      return undefined;
    }
    const dmgCodeObject = config.get('public.dmGatewayExpiration')[dmgCode];
    if (
      dmgCodeObject &&
      domainMatchesList(
        domain,
        dmgCodeObject.includeDomains,
        dmgCodeObject.excludeDomains
      )
    ) {
      return dmgCodeObject;
    } else {
      return undefined;
    }
  }
);

export const TRACK_NAV_ITEM_CLICK = 'TRACK_NAV_ITEM_CLICK';

export function trackNavItemClick(eventAction, eventLabel) {
  return {
    type: TRACK_NAV_ITEM_CLICK,
    eventAction,
    eventLabel,
  };
}

export const getCreditsRemaining = createSelector(
  isEmp,
  getMembership,
  (isEmp, membership) => {
    return isEmp
      ? membership.availableTokenQuantity
      : membership.membershipCredits;
  }
);

export const getFaqLink = createSelector(
  (state) => state.domain.tld,
  (domain) => config.get('public.faqLinks')[domain]
);

export const LOAD_ORDER_IF_MONDIAL_RELAY_REQUEST =
  'LOAD_ORDER_IF_MONDIAL_RELAY_REQUEST';
export const LOAD_ORDER_IF_MONDIAL_RELAY_SUCCESS =
  'LOAD_ORDER_IF_MONDIAL_RELAY_SUCCESS';
export const LOAD_ORDER_IF_MONDIAL_RELAY_FAILURE =
  'LOAD_ORDER_IF_MONDIAL_RELAY_SUCCESS';

export function loadOrderIfMondialRelay(orderId) {
  // Checks whether the order is a Mondial Relay or not
  return (dispatch) => {
    return dispatch({
      bentoApi: {
        endpoint: `accounts/me/orders/${orderId}?includeCustomerRetailPoint=true`,
        method: 'GET',
        actions: [
          LOAD_ORDER_IF_MONDIAL_RELAY_REQUEST,
          LOAD_ORDER_IF_MONDIAL_RELAY_SUCCESS,
          LOAD_ORDER_IF_MONDIAL_RELAY_FAILURE,
        ],
      },
    });
  };
}

export const SEND_CRL8_ORDER_DATA_SUCCESS = 'SEND_CRL8_ORDER_DATA_SUCCESS';

export function sendCrl8OrderData(order) {
  return async (dispatch) => {
    if (!window.crl8) {
      return;
    }

    const { orderLines } = order;
    window.crl8.pixel('addTransactionInfo', {
      transactionId: order.orderId,
      cartValue: order.total,
      taxValue: order.tax,
      shippingValue: order.shipping,
      shippingCity: order.shippingAddress.city || '',
      shippingCountry: order.shippingAddress.countryCode || '',
    });
    for (let i = 0; i < orderLines.length; i++) {
      const orderLine = orderLines[i];
      window.crl8.pixel('addProduct', {
        productId: orderLine.productId,
        productName: orderLine.label,
        price: orderLine.purchaseUnitPrice,
        quantity: orderLine.quantity,
      });
    }
    window.crl8.pixel('send');
    return dispatch({ type: SEND_CRL8_ORDER_DATA_SUCCESS, payload: order });
  };
}

/**
 * Return an array with countries for sepa payment method
 */
export const getSepaCountryList = createSelector(
  getStorePaymentSettings,
  (paymentSettings) => {
    if (paymentSettings && paymentSettings.sepa) {
      const countries = new Set(paymentSettings.sepa.countries);
      // We could just return `paymentSettings.sepa.countries`, but this will
      // filter out anything bogus and preserve the ordering we want.
      return allCountries.filter((country) => countries.has(country));
    }
    return [];
  }
);

export const getPaymentTypes = createSelector(
  (state, { isAfterPayEnabled, isCashAppEnabled, isKlarnaEnabled }) =>
    getStorePaymentTypes(state, {
      isAfterPayEnabled,
      isCashAppEnabled,
      isKlarnaEnabled,
    }),
  getStorePaymentSettings,
  (state, props) => props.address || {},
  filterPaymentTypesByAddress
);

export const isRihannasPickProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.rihannasPick')
      );
    }
    return false;
  }
);

export const isNewProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(config.get('public.productTags.new'));
    }
    return false;
  }
);

export const isBestSellerProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.bestSeller')
      );
    }
    return false;
  }
);

export const isBackInStockProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.backInStock')
      );
    }
    return false;
  }
);

export const isTopRatedProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.topRated')
      );
    }
    return false;
  }
);

export const isTrendingProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.trending')
      );
    }
    return false;
  }
);

export const isClfProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(config.get('public.productTags.clf'));
    }
    return false;
  }
);

export const isProductCustomizable = createSelector(
  (state, props) => props.product,
  (product) =>
    product && product.tag_id_list
      ? product.tag_id_list.includes(
          config.get('public.productTags.customizable')
        ) ||
        product.tag_id_list.includes(
          config.get('public.productTags.customizable').toString()
        ) // for search result
      : false
);

export const isUnlockedXclusiveProduct = createSelector(
  (state, props) => props.product,
  isBilledEmpVip,
  (product, isBilledEmpVip) => {
    return (
      isBilledEmpVip &&
      product.tag_id_list &&
      product.tag_id_list.includes(
        config.get('public.productTags.unlockedXclusive')
      )
    );
  }
);

export const isFWProduct = createSelector(
  (state, props) => props.product,
  (state) => state.domain.tld,
  (product, tld) => {
    if (product.featured_product_location_id_list) {
      // List available in product API data
      return product.featured_product_location_id_list.includes(
        config.get('public.featuredProductLists.fw')[tld]
      );
    }
    return false;
  }
);

export const isYouniversalProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(
        config.get('public.productTags.youniversal')
      );
    }
    return false;
  }
);

export const isProductTaggedGenericSoldOut = (tagIdList) => {
  const fakeSoldOutTagId = config.get('public.productTags.fakeSoldOut');
  return tagIdList && tagIdList.includes(fakeSoldOutTagId);
};

export function getLinkedProduct(masterProduct) {
  let linkedProduct = masterProduct.linked_master_product
    ? masterProduct.linked_master_product
    : null;
  if (Array.isArray(linkedProduct) && linkedProduct.length > 0) {
    linkedProduct = linkedProduct[0];
  }
  return linkedProduct;
}

export const isXtraVipBox = createSelector(
  (state, props) => props.product,
  (product) => {
    const xtraVipBoxTag = config.get('public.productTags.xtraVipBox');
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(xtraVipBoxTag);
    }
    return false;
  }
);

// check for fake sold out tag, then auto sell out VIP box product
export const isAutoSoldOut = createSelector(
  (state, props) => props.product,
  isXtraVipBox,
  getNewDateFunction,
  (masterProduct, xtraVipBox, newDate) => {
    const autoSellOutDate = config.get('public.vipAutoSellOut.date');
    const autoSellOutEnabled = config.get('public.vipAutoSellOut.enabled');
    // only apply auto sell out on xtraVipBox
    if (
      xtraVipBox &&
      autoSellOutEnabled &&
      autoSellOutDate != null &&
      isProductTaggedGenericSoldOut(masterProduct.tag_id_list)
    ) {
      const date = newDate();
      const pacificDayString = toPacificDateString(date, 'D');
      const pacificDay = parseInt(pacificDayString, 10);

      return pacificDay >= autoSellOutDate;
    }
    // do normal fake soldout of product
    return isProductTaggedGenericSoldOut(masterProduct.tag_id_list);
  }
);

export const isFakeSoldOut = createSelector(
  (state, props) => props.product,
  isAutoSoldOut,
  (masterProduct, autoSoldOut) => autoSoldOut
);

export const isUnbreakableSet = createSelector(
  (state, props) => props.product,
  (product) => {
    const unbreakableSetTag = config.get('public.productTags.unbreakableSet');
    if (product.tag_id_list) {
      // List available in product API data
      return product.tag_id_list.includes(unbreakableSetTag);
    }
    return false;
  }
);

/**
 * Identifies is a product is tagged as sold out
 * for either ['Sold Out - VIP', or 'Sold Out - Lead']
 *
 * @param {Object} product - product object
 * @return {boolean} returns true if a product is tagged as
 *                   either 'Sold Out - VIP' or 'Sold Out - Lead'
 *                   false otherwise
 *
 * */

export const isProductTaggedLeadOrVipSoldOut = createSelector(
  (state, props) => getMembership(state),
  (state, props) => props.product,
  (membership, product) => {
    const soldOutLeadTag = config.get('public.productTags.soldOutLead');
    const soldOutVipTag = config.get('public.productTags.soldOutVip');
    const { tag_id_list: tagIdList } = product;

    if (membership.isLead && product.tag_id_list) {
      return tagIdList.includes(soldOutLeadTag);
    } else if (membership.isVip && product.tag_id_list) {
      return tagIdList.includes(soldOutVipTag);
    }

    return false;
  }
);

export const isSoldOut = createSelector(
  (state, props) => props.product,
  (state, props) => (props.isOnGrid ? props.isOnGrid : false),
  isExclusiveProduct,
  isExclusiveSoldOut,
  isFakeSoldOut,
  isUnbreakableSet,
  isXtraVipBox,
  isPreorderable,
  isProductTaggedLeadOrVipSoldOut,
  getNewDateFunction,
  (
    product,
    isOnGrid,
    isExclusive,
    isExclusiveSoldOut,
    isFakeSoldOut,
    isUnbreakableSet,
    isXtraVipBox,
    isPreorderable,
    isProductTaggedLeadOrVipSoldOut,
    now
  ) => {
    const linkedProduct = getLinkedProduct(product);
    if (linkedProduct && !isOnGrid) {
      if (
        isFakeSoldOut &&
        isProductTaggedGenericSoldOut(linkedProduct.tag_id_list)
      ) {
        return true;
      }

      const actualQuantity = isPreorderable
        ? product.available_quantity_preorder_master
        : product.available_quantity_master;
      const actualLinkedProductQuantity = isPreorderable
        ? product.available_quantity_preorder_master
        : linkedProduct.available_quantity_master;
      return actualQuantity <= 0 && actualLinkedProductQuantity <= 0;
    }

    if (
      isProductTaggedLeadOrVipSoldOut &&
      isProductTaggedGenericSoldOut(product.tag_id_list)
    ) {
      return isFakeSoldOut;
    }
    // check if it is tagged as sold out on either vip/lead
    if (isProductTaggedLeadOrVipSoldOut) {
      return true;
    }

    if (isFakeSoldOut) {
      return true;
    }

    if (isExclusive && isExclusiveSoldOut) {
      return true;
    }
    if (isProductSet(product)) {
      if (
        product.available_quantity_any_profile != null &&
        !product.products_in_set
      ) {
        return false;
      } else if (product.available_quantity_any_profile != null) {
        return product.available_quantity_any_profile <= 0;
      } else if (product.products_in_set) {
        return product.products_in_set.some(
          /* FIXME: Unfortunately `available_quantity_master` does not take into
           account the inventory of all colors for a single set item, which is
           what we actually want here. */
          (setItem) => {
            const isItemInSetPreorderable = checkPreorderForSingleItem({
              product: setItem,
              now,
            });
            const colors = setItem.related_product_id_object_list;
            const hasColors = colors && colors.length;
            if (hasColors) {
              // Every color part of the set is sold out
              return colors.every((color) => {
                let isColorPartOfSet = true;
                if (color.tag_id_list) {
                  const isPartOfXtraVipBox = color.tag_id_list.includes(
                    config.get('public.productTags.xtraVipBox')
                  );
                  /* since we no longer check the hideFromGrid tag on individual items for xtra VIP box
                   * we will use the xtraVipBoxTag instead
                   */
                  if (isUnbreakableSet && isXtraVipBox) {
                    isColorPartOfSet = isPartOfXtraVipBox;
                  }
                }
                const sizes = color.product_id_object_list;
                // Every size in this color is sold out, only check if the color part of the set
                if (isColorPartOfSet) {
                  return sizes.every((size) =>
                    isItemInSetPreorderable
                      ? size.available_quantity_preorder <= 0
                      : size.available_quantity <= 0
                  );
                }
                return true;
              });
            } else {
              return isItemInSetPreorderable
                ? setItem.available_quantity_preorder_master <= 0
                : setItem.available_quantity_master <= 0;
            }
          }
        );
      }
    }
    const actualQuantity = isPreorderable
      ? product.available_quantity_preorder_master
      : product.available_quantity_master;
    return actualQuantity <= 0;
  }
);

export const isComingSoonProduct = createSelector(
  (state, props) => props.product,
  (product) => {
    const comingSoonTag = config.get('public.productTags.comingSoon');
    if (product.tag_id_list) {
      return product.tag_id_list.includes(comingSoonTag);
    } else if (product.tagIdList) {
      return product.tagIdList.includes(comingSoonTag);
    }
    return false;
  }
);

export function getProductWarehouseQuantity(
  product,
  warehouseIds,
  isSoldOut,
  isPreorderable
) {
  if (isSoldOut) {
    return 0;
  }

  if (isPreorderable) {
    return product.available_quantity_preorder_master;
  }

  const warehouses = product.warehouse_id_object_list;
  if (warehouses == null) {
    return product.available_quantity_master;
  }

  const quantity = warehouses
    .filter((warehouse) => {
      return warehouseIds.find((id) => warehouse.warehouse_id === id);
    })
    .reduce((total, warehouse) => {
      return warehouse.available_quantity + total;
    }, 0);
  return Math.max(0, quantity);
}

export const getWarehouseQuantity = createSelector(
  (state, props) => props.product,
  (state) => getWarehouseIds(state),
  isSoldOut,
  isPreorderable,
  (product, warehouseIds, isSoldOut, isPreorderable) => {
    return getProductWarehouseQuantity(
      product,
      warehouseIds,
      isSoldOut,
      isPreorderable
    );
  }
);

export const shouldHideRelatedProducts = createSelector(
  (state, props) => props.product,
  (product) => {
    const hideRelatedTagId = config.get('public.productTags.hideRelated');
    if (product.tag_id_list) {
      return product.tag_id_list.includes(hideRelatedTagId);
    } else if (product.tagIdList) {
      return product.tagIdList.includes(hideRelatedTagId);
    }
  }
);

export const shouldProductUseCustomTag = createSelector(
  (state, props) => props.product,
  (product) => {
    const customTag = config.get('public.productTags.customTag');
    const tagIdList = product?.tagIdList || product?.tag_id_list;
    return tagIdList
      ? tagIdList.includes(customTag) ||
          tagIdList.includes(customTag.toString())
      : false;
  }
);

export const isCurvySize = createSelector(
  (state, props) => props.product,
  (product) => {
    const curvySizeTag = config.get('public.productTags.plusSize');
    if (product && product.tag_id_list) {
      return product.tag_id_list.includes(curvySizeTag);
    }
    return false;
  }
);

export const getAdditionalSaleDiscount = createSelector(
  (state, props) => state.checkout.discounts,
  (discounts) => {
    const additionalSaleDiscount = config.get('public.additionalSaleDiscount');
    return discounts.find(
      (discount) => discount.promoCode === additionalSaleDiscount.promoCode
    );
  }
);

export const getProductReviews = createSelector(
  (state, props) => props.product,
  (state, props) => !!props.isSet,
  (state) => state.reviews,
  (product, isSet, reviews) => {
    if (!product || !reviews || (isSet && !reviews.byMasterProductId)) {
      return null;
    }

    if (isSet || reviews?.byMasterProductId) {
      reviews = reviews.byMasterProductId[product.master_product_id];
    }
    const groupCode = (product.group_code || '').toUpperCase();
    const masterProductId = Number(product.master_product_id);

    const shouldReturnReviews =
      (groupCode && reviews?.groupCode === groupCode) ||
      (masterProductId && reviews?.masterProductId === masterProductId) ||
      (reviews?.masterProductId &&
        product.bundle_product_id_list &&
        product.bundle_product_id_list.includes(reviews?.masterProductId));
    return shouldReturnReviews ? reviews : null;
  }
);

function buildProductList(storeKey, products, startPage = 1) {
  const allProducts = [];
  const filterProducts = products.masterProductIdsByFilter[storeKey];
  if (!filterProducts) {
    return [];
  }
  let pageNumber = startPage || 1;
  while (true) {
    const page = filterProducts.pages[pageNumber];
    if (!page || !page.responseIds.length) {
      break;
    }
    allProducts.push(...page.responseIds);
    pageNumber += 1;
  }
  // need to investigate this some more
  // randomly the server will respond with a product we've already received on another page
  // but we will dedupe to make sure we don't display it twice
  const removeDups = new Set([...allProducts]);
  const dedupedArray = Array.from(removeDups);
  return dedupedArray.map((productId) => products.byMasterProductId[productId]);
}

const EMPTY_REFINEMENTS = {
  categories: [],
  collections: [],
  colorTags: [],
  colors: [],
  sizes: [],
  braSizes: [],
  braletteSizes: [],
  lingerieSizes: [],
  undieSizes: [],
};

export const getProductFilterState = createSelector(
  (state, ownProps) => ownProps.filters,
  getUserProfileProductFilters,
  getStateDependentProductFilters,
  (state) => state.products,
  (state, ownProps) => ownProps.startPage || 1,
  (inputFilters, filters, requestFilters, products, startPage) => {
    const requestParams = filtersToRequestParams(requestFilters);
    const storeKey = requestParamsToStoreKey(requestParams);
    const aggregationKey = requestParamsToAggregationKey(requestParams);
    const productList = buildProductList(storeKey, products, startPage);
    const filterProducts = products.masterProductIdsByFilter[storeKey];
    return {
      inputFilters,
      filters,
      didTransformFilters: filters !== inputFilters,
      requestParams,
      storeKey,
      canPaginate: filterProducts ? filterProducts.canPaginate : false,
      refinements: products.aggregations[aggregationKey] || EMPTY_REFINEMENTS,
      products: productList,
    };
  }
);

export const isDiscreetPackagingInOrder = createSelector(
  (state) => state.checkout.order,
  (state, props) => props.order,
  (orderFromState, orderFromProps) => {
    const order = orderFromState || orderFromProps;
    return !!(order && order.isDiscreetPackaging);
  }
);

export const isXtraVipBoxInOrder = createSelector(
  (state) => state.checkout.order,
  (state, props) => props.order,
  (orderFromState, orderFromProps) => {
    const xtraVipBoxTag = config.get('public.productTags.xtraVipBox');
    const order = orderFromState || orderFromProps;
    return (
      order &&
      order.orderLines.some(
        (item) =>
          item.tagIds &&
          item.tagIds.includes(xtraVipBoxTag) &&
          isProductSet(item)
      )
    );
  }
);

export const getMemberJoinDate = createSelector(
  (state) => getMembership(state).dateTimeAdded,
  (dateTimeAdded) => (dateTimeAdded ? parseDate(dateTimeAdded) : null)
);
